import { useState } from 'react';
import { useParams } from '@tanstack/react-router';
import { CircleNotchIcon } from '@phosphor-icons/react';
import { Badge } from 'src/components/ui/badge';
import { Button } from 'src/components/ui/button';
import { Action, Subject } from '@erp/authz';
import {
  AgentBuilder,
  type AgentBuilderState,
  type AgentPermission,
} from '@/components/agents';

interface AgentBuilderDetail {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
}

const MOCK_AGENT: AgentBuilderDetail = {
  id: '1',
  name: 'Sales Assistant',
  description: 'Helps with customer inquiries and order processing',
  isActive: true,
};

function createEmptyAgentState(): AgentBuilderState {
  return {
    name: '',
    description: '',
    prompt: '',
    enabledTools: new Set<string>(),
    permissions: [] as AgentPermission[],
  };
}

const MOCK_AGENT_STATE: AgentBuilderState = {
  name: 'Sales Assistant',
  description: 'Helps with customer inquiries and order processing',
  prompt:
    'You are a helpful sales assistant. Help customers with their orders and answer their questions professionally.',
  enabledTools: new Set(['read-customers', 'search-products']),
  permissions: [
    { action: Action.Read, subject: Subject.Customer },
    { action: Action.Read, subject: Subject.Product },
  ],
};

export function AgentBuilderDetailLayoutPage() {
  const { agentId } = useParams({
    from: '/authenticated/agents/$agentId',
  });

  const isCreateMode = agentId === 'new';
  const [isLoading] = useState(false);

  const agent = isCreateMode ? null : MOCK_AGENT;

  const [agentState, setAgentState] = useState<AgentBuilderState>(() =>
    isCreateMode ? createEmptyAgentState() : MOCK_AGENT_STATE,
  );

  const handleSave = () => {
    console.log('Saving agent:', agentState);
  };

  if (!isCreateMode && isLoading) {
    return (
      <div className="p-2">
        <div className="flex items-center justify-center py-12">
          <CircleNotchIcon className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!isCreateMode && !agent) {
    return (
      <div className="p-2">
        <div className="text-center py-12">
          <p className="text-destructive">Failed to load agent</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-2">
      <div className="mb-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold">
              {isCreateMode ? 'Create New Agent' : agent?.name}
            </h1>
            {!isCreateMode && agent?.description && (
              <p className="text-muted-foreground mt-1">{agent.description}</p>
            )}
            {isCreateMode && (
              <p className="text-muted-foreground mt-1">
                Configure your new AI agent
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {!isCreateMode && (
              <Badge variant={agent?.isActive ? 'default' : 'secondary'}>
                {agent?.isActive ? 'Active' : 'Inactive'}
              </Badge>
            )}
            <Button onClick={handleSave}>
              {isCreateMode ? 'Create Agent' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </div>

      <AgentBuilder value={agentState} onChange={setAgentState} />
    </div>
  );
}
